<script setup lang="ts">
import BaseLoader from '@/components/BaseComponents/BaseLoader.vue';
import SelectWeek from '@/components/Planning/SelectWeek.vue';
import { useEventBus } from '@/composables/useEventBus';
import { usePermissions } from '@/composables/usePermissions';
import { usePlanning } from '@/composables/usePlanning';
import type { MessageSchema } from '@/i18n';
import { usePlanningStore } from '@/stores/planningStore';
import { usePostitsStore } from '@/stores/postitsStore';
import { useUserStore } from '@/stores/useUserStore';
import type { User } from '@/types/api';
import { hasSubscriptionFeature } from '@/utils/auth';
import WeekEmployeeView from '@/views/Employee/WeekEmployeeView.vue';
import WeekPostitsView from '@/views/Employee/WeekPostitsView.vue';
import WeekMenuView from '@/views/Planning/WeekMenuView.vue';
import { storeToRefs } from 'pinia';
import Button from 'primevue/button';
import TabPanel from 'primevue/tabpanel';
import TabView from 'primevue/tabview';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const eventBus = useEventBus();

eventBus.on('onboarding:set-planning-tab', (tabIndex: number) => {
  activeTab.value = tabIndex;
});

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const route = useRoute();
const toast = useToast();

// Initialize activeTab from route query or localStorage
const getInitialTab = () => {
  const tabFromQuery = route.query.tab;
  if (tabFromQuery && typeof tabFromQuery === 'string') {
    const tabNumber = parseInt(tabFromQuery, 10);
    if (!isNaN(tabNumber) && tabNumber >= 0 && tabNumber <= 2) {
      return tabNumber;
    }
  }
  return Number(localStorage.getItem('planningActiveTab')) || 0;
};

const activeTab = ref(getInitialTab());

const userStore = useUserStore(); // Initialize user store
const user = ref(userStore.user); // Get user
const planningStore = usePlanningStore();
const { isLoading } = storeToRefs(planningStore);
const { can } = usePermissions();
const canDownloadWeekPdf = computed(() => can('planning', 'update'));

watch(activeTab, async (val) => {
  localStorage.setItem('planningActiveTab', val.toString());
  // Assuming "Tasks" is the 3rd tab (index 2)
  if (val === 2) {
    const restaurantId = user.value?.restaurants?.[0]?.id;
    if (restaurantId) {
      const postitsStore = usePostitsStore();
      postitsStore.shouldRefetch = true;
      await postitsStore.fetchInitialWindow(restaurantId);
    }
  }
});

// watch(planningStore.weekRange, async (newRange) => {
//   console.log("Change planning Store.weekrange")

//   const restaurantId = user.value?.restaurants[0]?.id || null
//   await planningStore.fetchMenusIfNeeded(newRange.start.toISOString().split('T')[0], newRange.end.toISOString().split('T')[0], restaurantId);
// }, { deep: true, immediate: true });

onMounted(async () => {
  planningStore.fetchMenusByDateRange(
    planningStore.weekRange.start.toISOString().split('T')[0],
    planningStore.weekRange.end.toISOString().split('T')[0],
    user.value?.restaurants[0]?.id || null,
  );
});

const { generatePDF } = usePlanning();

const handleGeneratePDF = async () => {
  const success = await generatePDF(user.value as User);
  if (success) {
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('planning.pdfDownloadSuccess'),
      life: 3000,
    });
  } else {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('planning.pdfDownloadError'),
      life: 3000,
    });
  }
};

const hasModuleStaff = computed(() => hasSubscriptionFeature(user.value, 'module_staff', true));

const weekTotalPrice = computed(() => {
  const startDate = planningStore.weekRange.start;
  const endDate = planningStore.weekRange.end;
  const days: Date[] = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  const weekDayStrings = days.map((date) => date.toISOString().split('T')[0]);
  return planningStore.plannedMenus
    .filter((menu) => weekDayStrings.includes(menu.date))
    .reduce((sum, menu) => sum + (Number(menu.totalPrice) || 0), 0);
});
</script>

<template>
  <main>
    <TabView v-model:active-index="activeTab" :pt="{ inkbar: { class: 'hidden' } }" :pt-options="{ mergeProps: true }">
      <TabPanel
        :pt="{
          header: { class: '!w-1/2' },
          headerAction: { class: '!justify-center', id: 'onboarding-tab-week-menu-button' },
          content: { class: 'onboarding-tab-week-menu !px-0' },
        }"
        :pt-options="{ mergeProps: true }"
        :header="t('planning.tabs.weekMenu')"
      >
        <SelectWeek
          :week-number="planningStore.currentWeekNumber"
          :week-range="planningStore.weekRange"
          @previous-week="planningStore.goToPreviousWeek"
          @next-week="planningStore.goToNextWeek"
        />
        <div class="mx-8 my-2 flex flex-row items-center justify-between gap-4">
          <div v-if="userStore.activeRestaurant?.role === 'admin' || userStore.activeRestaurant?.role === 'manager'" class="font-semibold text-sm">
            {{
              t('planning.weekTotalPrice', {
                price: weekTotalPrice.toFixed(2),
                currency: user?.active_restaurant?.currency_symbol || 'CHF',
              })
            }}
          </div>
          <Button
            v-if="canDownloadWeekPdf"
            rounded
            link
            :pt="{ root: { class: '!p-1.5 !w-10 !h-10' } }"
            :pt-options="{ mergeProps: true }"
            @click="handleGeneratePDF"
          >
            <!-- PrimeVue default icon -->
            <i class="pi pi-file-pdf text-xl text-primary-950"></i>
          </Button>
        </div>
        <div v-if="isLoading" class="mx-auto mt-6 flex justify-center">
          <BaseLoader />
        </div>
        <div v-else>
          <WeekMenuView :week-range="planningStore.weekRange" />
        </div>
      </TabPanel>
      <TabPanel
        :header="t('planning.tabs.presences')"
        :pt="{
          header: { class: '!w-1/2' },
          headerAction: { class: '!justify-center', id: 'onboarding-tab-presences-button' },
          content: { class: '!px-0 onboarding-tab-presences' },
        }"
        :disabled="!hasModuleStaff"
      >
        <SelectWeek
          :week-number="planningStore.currentWeekNumber"
          :week-range="planningStore.weekRange"
          @previous-week="planningStore.goToPreviousWeek"
          @next-week="planningStore.goToNextWeek"
        />
        <div>
          <WeekEmployeeView v-if="hasModuleStaff" :week-range="planningStore.weekRange" />
        </div>
      </TabPanel>
      <TabPanel
        :header="t('planning.tabs.tasks')"
        :pt="{
          header: { class: '!w-1/2' },
          headerAction: { class: '!justify-center', id: 'onboarding-tab-tasks-button' },
          content: { class: '!px-0 onboarding-tab-tasks' },
        }"
        :disabled="!hasModuleStaff"
      >
        <!-- <SelectWeek :weekNumber="planningStore.currentWeekNumber" :weekRange="planningStore.weekRange"
          @previous-week="planningStore.goToPreviousWeek" @next-week="planningStore.goToNextWeek" /> -->
        <WeekPostitsView v-if="hasModuleStaff" />
      </TabPanel>
    </TabView>
  </main>
</template>
